import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useState } from "react";
import { v4 as uuid } from "uuid";
import {
  BseStatusDto,
  EmailInviteLanguage,
  MinimalOrgDto,
  OrganizationEmailInviteDto,
  useGetOrganizationQuery,
  useListPersonsQuery,
} from "../../../../../autogen/bff-api";
import { ModalSkeleton } from "../../../../../common/ModalSkeleton";
import { Paging } from "../../../../../common/Paging";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import { editBseThunk } from "../../../../../common/redux/thunks/basic-sourcing-event/edit-bse-thunk";
import { displayPersonNameWithEmail } from "../../../../contracts/view-single/sharing/AddExternalParticipantModal";

const limit = 10;

export const InviteOrganizationModal = ({
  minimalOrg,
  onClose,
  eventStatus,
}: {
  minimalOrg?: MinimalOrgDto;
  onClose: () => void;
  eventStatus: BseStatusDto;
}) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [skip, setSkip] = useState(0);
  const { data, isLoading, isFetching } = useListPersonsQuery(
    minimalOrg ? { orgIds: [minimalOrg.id], limit: 10, skip } : skipToken
  );

  const { data: organizationProfile, isLoading: isLoadingOrgProfile } = useGetOrganizationQuery(
    minimalOrg?.id ? { orgId: minimalOrg.id } : skipToken
  );

  const [manualEmailInvites, setManualEmailInvites] = useState<Omit<OrganizationEmailInviteDto, "status">[]>([]);
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [selectedContactPersonEmails, setSelectedContactPersonEmails] = useState<string[]>([]);

  const linkedEntryContactPersons =
    organizationProfile?.linkedEntries?.flatMap((entry) => entry.contactPersons ?? []) ?? [];
  // Use email as unique key (in case of duplicates across entries)
  const contactPersons = linkedEntryContactPersons.filter(
    (p, idx, arr) => arr.findIndex((x) => x.email === p.email) === idx
  );

  const createOrganizationInvite = async () => {
    if (!minimalOrg?.id) throw Error("Organization id was unexpectedly undefined");

    // Combine manual and contact person invites
    const orgEmailInvites = [
      ...contactPersons
        .filter((p) => selectedContactPersonEmails.includes(p.email))
        .map((p) => ({
          email: p.email,
          language: EmailInviteLanguage.No,
        })),
      ...manualEmailInvites.map((invite) => ({
        email: invite.email,
        language: invite.language,
      })),
    ];

    await dispatch(
      editBseThunk({
        command: {
          type: "AddInvitedOrganization",
          value: {
            organizationId: minimalOrg.id,
            personIds: selectedPersonIds,
            emailInvites: orgEmailInvites,
          },
        },
      })
    );
  };

  const createInvites = async () => {
    try {
      await createOrganizationInvite();
      handleClose();
    } catch (error) {
      // Error handling - don't close modal if invite creation fails
      toast({
        title: t("Failed to create invites"),
        description: t("Something went wrong, please try again"),
        status: "error",
      });
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const areEmailInvitesValid = (): boolean => {
    return manualEmailInvites.every((invite) => invite.email.trim() !== "" && isValidEmail(invite.email));
  };

  const handleClose = () => {
    setSelectedPersonIds([]);
    setManualEmailInvites([]);
    setSelectedContactPersonEmails([]);
    onClose();
  };

  // Computed values for button state and tooltip
  const hasSelectedPersons = selectedPersonIds.length > 0;
  const hasSelectedContactPersons = selectedContactPersonEmails.length > 0;
  const hasValidManualInvites = areEmailInvitesValid() && manualEmailInvites.length > 0;
  const hasInvalidManualEmails = manualEmailInvites.length > 0 && !areEmailInvitesValid();
  const isEventCompleted = eventStatus === "Awarded" || eventStatus === "Completed";

  const hasAnySelection = hasSelectedPersons || hasSelectedContactPersons || hasValidManualInvites;
  const isButtonDisabled = !hasAnySelection || hasInvalidManualEmails || isEventCompleted;

  const getTooltipMessage = (): string => {
    if (isEventCompleted) {
      return t("Invitations cannot be created for awarded or completed events");
    }
    if (!hasAnySelection) {
      return t("Select at least one person or add a valid email invite");
    }
    if (manualEmailInvites.length > 0 && !areEmailInvitesValid()) {
      return t("All email addresses must be valid");
    }
    return "";
  };

  return (
    <Modal isOpen={!!minimalOrg} onClose={handleClose} size="3xl">
      <ModalOverlay />
      <ModalContent width="full" maxHeight="80%" padding="5" overflow="hidden">
        <ModalCloseButton />
        <ModalBody height="full" overflowY="auto">
          <Flex height={"full"} flexDirection={"column"}>
            <Heading as="h2" size="md" pb="2">
              {t("Select persons to invite for")} {minimalOrg?.name}
            </Heading>
            {(isLoading || isFetching || isLoadingOrgProfile) && <ModalSkeleton />}
            {!(isLoading || isFetching || isLoadingOrgProfile) && minimalOrg && data && (
              <Flex flexDirection="column" pt="5">
                <FormLabel fontSize="sm">{t("Persons with a SourceMagnet account")}</FormLabel>
                <CheckboxGroup colorScheme="teal">
                  {data.persons.map((p) => (
                    <Checkbox
                      key={p.id}
                      isChecked={!!selectedPersonIds.find((id) => id === p.id)}
                      onChange={(e) => {
                        setSelectedPersonIds((personIds) => {
                          if (e.target.checked) return [...personIds.filter((id) => id !== p.id), p.id];
                          return [...personIds.filter((id) => id !== p.id)];
                        });
                      }}
                    >
                      {displayPersonNameWithEmail(p)}
                    </Checkbox>
                  ))}
                </CheckboxGroup>
                <Paging
                  limit={limit}
                  skip={skip}
                  setSkip={(skip) => setSkip(skip)}
                  isLoading={isLoading}
                  count={data.count}
                />
                {/* Contact persons from linked entries */}
                {contactPersons.length > 0 && (
                  <Box my={4} py={4} borderTopWidth="1px" borderColor="smBorder">
                    <FormControl size="sm" pt="2">
                      <FormLabel fontSize="sm">{t("Contact persons registered by your organization")}</FormLabel>
                      <CheckboxGroup
                        colorScheme="teal"
                        value={selectedContactPersonEmails}
                        onChange={(emails) => setSelectedContactPersonEmails(emails as string[])}
                      >
                        {contactPersons.map((p) => (
                          <Checkbox
                            key={p.email}
                            value={p.email}
                            isChecked={selectedContactPersonEmails.includes(p.email)}
                          >
                            {`${p.name ?? ""} (${p.email})`}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    </FormControl>
                  </Box>
                )}
                <Box borderTopWidth="1px" borderBottomWidth="1px" borderColor="smBorder">
                  <FormControl size="sm" pt="2">
                    <FormLabel fontSize="sm">{t("Invite additional users by email")}</FormLabel>
                    {manualEmailInvites.map((invite) => (
                      <Flex key={invite.id} mb={2} alignItems="center">
                        <Input
                          size="sm"
                          rounded="md"
                          value={invite.email}
                          isInvalid={invite.email.trim() !== "" && !isValidEmail(invite.email)}
                          onChange={(e) =>
                            setManualEmailInvites((invites) =>
                              invites.map((item) => (item.id === invite.id ? { ...item, email: e.target.value } : item))
                            )
                          }
                          mr={2}
                        />
                        <Select
                          size="sm"
                          rounded="md"
                          width="200px"
                          value={invite.language}
                          onChange={(e) =>
                            setManualEmailInvites((invites) =>
                              invites.map((item) =>
                                item.id === invite.id
                                  ? { ...item, language: e.target.value as EmailInviteLanguage }
                                  : item
                              )
                            )
                          }
                          mr={2}
                        >
                          <option value="No">{t("Norwegian")}</option>
                          <option value="En">{t("English")}</option>
                        </Select>
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() =>
                            setManualEmailInvites((invites) => invites.filter((item) => item.id !== invite.id))
                          }
                        >
                          {t("Remove")}
                        </Button>
                      </Flex>
                    ))}
                    <Button
                      size="sm"
                      onClick={() =>
                        setManualEmailInvites((invites) => [
                          ...invites,
                          {
                            id: uuid(),
                            email: "",
                            language: EmailInviteLanguage.No,
                            organizationId: minimalOrg?.id,
                          },
                        ])
                      }
                      mt={1}
                    >
                      {t("Add email")}
                    </Button>
                    <FormHelperText fontSize="xs" py={2}>{`${t(
                      "Only use this option if you want to invite users that are not listed above"
                    )}.
                    ${t(
                      "One of the existing admins will have to grant the new users access to their organization"
                    )}.`}</FormHelperText>
                  </FormControl>
                </Box>
                <FormControl flexDirection="column" alignItems="end" pt="5">
                  <Tooltip isDisabled={!isButtonDisabled} label={getTooltipMessage()} placement="top" hasArrow>
                    <Button
                      isDisabled={isButtonDisabled}
                      onClick={async () => {
                        await createInvites();
                      }}
                      colorScheme="blue"
                    >
                      {t("Create invite")}
                    </Button>
                  </Tooltip>
                  {(eventStatus === "Published" || eventStatus === "DeadlineHasPassed") && (
                    <FormHelperText color="orange">
                      {t("The sourcing event is published and invitations will be sent at once")}.
                    </FormHelperText>
                  )}
                </FormControl>
              </Flex>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
